import "./style.scss";

import { TableCell, TableRow } from "@mui/material";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ShowStatementsPublisher from "components/shared/ShowStatementsPublisher";
import { Spinner } from "components/shared/Spinner";
import TextButton from "components/shared/TextButton";
import type { IBeneficiar } from "containers/Beneficiars/types";
import { formatLocaleDateString, isNewDate } from "helpers/utils";
import { formatNumber } from "helpers/utils";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IStatement } from "store/beneficiaryStore";
import { resetStatement } from "store/statementsStore";
import { useAppDispatch } from "store/store";
import type { TFetchStatus } from "store/types";

import variables from "../../assets/styles/_variables.module.scss";

interface IStatementsTab {
    beneficiary: IBeneficiar;
    statements: IStatement[];
    statementsStatus: TFetchStatus;
    companyCurrency: string;
    usesMultipleCurrencies: boolean;
    activePeriod: number | undefined;
}

const StatementsTab = ({
    beneficiary,
    statements,
    statementsStatus,
    companyCurrency,
    usesMultipleCurrencies,
    activePeriod,
}: IStatementsTab) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [currentStatement, setCurrentStatement] = useState<IStatement>({} as IStatement);

    const isStatementCurrencyDefault = (beneficiary.statementCurrency ?? "") === companyCurrency;

    const renderStatementType = (type: string) => {
        if (type === "advance") {
            return <span>{t("StatementsTab.Advance")}</span>;
        }
        if (type === "title") {
            return <span>{t("StatementsTab.TitleStatement")}</span>;
        }
        if (type === "consolidated") {
            return <span>{t("StatementsTab.Consolidated")}</span>;
        }
    };

    const handleShowStatements = (statement: any) => {
        setCurrentStatement(statement);
        setIsOpen(true);
    };

    const toggleDrawer = (open: boolean) => {
        setIsOpen(open);
    };

    const statementsTableColumns: LastElementTableColumns[] = [
        { id: "name", label: t("StatementsTab.Title"), align: "left", sort: true },
        {
            id: "type",
            label: t("StatementsTab.Type"),
            align: "left",
            sort: true,
            format: (value: any) => renderStatementType(value),
        },
        {
            id: "period",
            label: t("StatementsTab.Period"),
            align: "left",
            sort: true,
            format: (value: any, id?: number, statement?: any) => {
                return (
                    <div>
                        {statement &&
                            ["title", "consolidated"].includes(statement.type) &&
                            `${formatLocaleDateString({ date: statement.periodStart })} → ${formatLocaleDateString({
                                date: statement.periodEnd,
                            })}`}
                        {statement.type === "advance" && "-"}
                    </div>
                );
            },
        },
        {
            id: "grossDuty",
            label: t("StatementsTab.Gross"),
            align: "right",
            format: (value: any) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
            sort: true,
        },
        {
            id: "action",
            label: "",
            align: "center",
            format: (value: any, id?: number, statement?: any) => (
                <TextButton
                    text={t("StatementsTab.SeeDetails")}
                    handleClick={() => handleShowStatements(statement)}
                    style={{ fontSize: "12px" }}
                    underlineOnHover
                />
            ),
        },
    ];

    const separatorContent = (statement: any) => (
        <TableRow>
            <TableCell style={{ padding: 0, border: "none" }} colSpan={5}>
                <div className="bpstatement-newperiod-separator">
                    <span>{new Date(statement.periodEnd).getUTCFullYear()}</span>
                </div>
            </TableCell>
        </TableRow>
    );

    // Needed to make sure statements are correctly ordered from newest to oldest so that the separator makes sense.
    const orderedStatements = useMemo(() => {
        return [...statements].sort(
            (a, b) => new Date(b.periodEnd).getUTCFullYear() - new Date(a.periodEnd).getUTCFullYear(),
        );
    }, [statements]);

    const onCloseStatementDrawer = () => {
        setIsOpen(false);
        setCurrentStatement({} as IStatement);
        dispatch(resetStatement());
    };

    return (
        <div className="statements-table">
            <ShowStatementsPublisher
                isOpen={isOpen}
                onCloseStatementDrawer={() => onCloseStatementDrawer()}
                currentStatement={currentStatement && currentStatement}
                beneficiary={beneficiary}
                isStatementCurrencyDefault={isStatementCurrencyDefault}
                usesMultipleCurrencies={usesMultipleCurrencies}
                companyCurrency={companyCurrency}
                activePeriod={activePeriod}
            />
            {statementsStatus === "pending" ? (
                <div style={{ display: "flex", justifyContent: "center" }}>
                    <Spinner />
                </div>
            ) : (
                <LastElementTable
                    columns={statementsTableColumns}
                    data={orderedStatements}
                    dataType="statements"
                    max={-1}
                    isNewDate={(row: any, index: number, data: any) => isNewDate(row, index, data, "periodEnd")}
                    separatorContent={(statement: any) => separatorContent(statement)}
                    onRowClick={(id: any, id2: any, type: any, row: any) => handleShowStatements(row)}
                    stickyFooter
                />
            )}
        </div>
    );
};

export default StatementsTab;
